import { useEffect, useState } from "react";

// Phoenix.edu's mobile action bar has a stable, documented selector. Privacy
// links in other fixed host footers are detected below as a semantic fallback.
const HOST_BOTTOM_ACTION_BAR_QUERY = "#stickyFooter.sticky";

export type HostBottomObstructionHeights = {
  bottomOffsetHeight: number;
  privacyFooterHeight: number;
};

const EMPTY_HOST_BOTTOM_OBSTRUCTION_HEIGHTS: HostBottomObstructionHeights = {
  bottomOffsetHeight: 0,
  privacyFooterHeight: 0,
};

function findHostBottomObstructionCandidates(): {
  candidates: Set<HTMLElement>;
  privacyFooters: Set<HTMLElement>;
} {
  const candidates = new Set(Array.from(document.querySelectorAll<HTMLElement>(HOST_BOTTOM_ACTION_BAR_QUERY)));
  const privacyFooters = new Set<HTMLElement>();

  for (const link of document.querySelectorAll<HTMLAnchorElement>("a[href]")) {
    const linkName = `${link.textContent ?? ""} ${link.getAttribute("aria-label") ?? ""}`;
    if (!/privacy\s+policy/i.test(linkName)) continue;

    let candidate: HTMLElement | null = link;
    while (candidate && candidate !== document.body) {
      const position = window.getComputedStyle(candidate).position;
      if (position === "fixed" || position === "sticky") {
        candidates.add(candidate);
        privacyFooters.add(candidate);
        break;
      }
      candidate = candidate.parentElement;
    }
  }

  return { candidates, privacyFooters };
}

function measureCandidateHeight(candidates: Set<HTMLElement>, maximumViewportFraction = 0.5): number {
  const root = document.getElementById("phoebe-chat-root");
  const viewportHeight = window.innerHeight;
  let maxHeight = 0;
  for (const element of candidates) {
    // Radix portals modal content outside #phoebe-chat-root. Exclude those
    // widget-owned surfaces before treating their Privacy Policy link as a
    // host-page obstruction.
    if (root?.contains(element) || element.closest(".aui-chat-shell, .aui-side-panel")) continue;
    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) continue;
    if (style.position !== "fixed" && style.position !== "sticky") continue;

    const rect = element.getBoundingClientRect();
    if (rect.bottom < viewportHeight - 4 || rect.top >= viewportHeight) continue;
    if (rect.width < Math.min(240, window.innerWidth * 0.6)) continue;
    if (rect.height > viewportHeight * maximumViewportFraction) continue;
    maxHeight = Math.max(maxHeight, Math.ceil(viewportHeight - Math.max(0, rect.top)));
  }

  return maxHeight;
}

export function measureHostBottomObstructionHeights(): HostBottomObstructionHeights {
  const { candidates, privacyFooters } = findHostBottomObstructionCandidates();

  const actionBarHeight = measureCandidateHeight(candidates);
  // Privacy notices grow substantially when browser text magnification causes
  // their legal copy to wrap. Treat a fixed/sticky ancestor of an actual
  // Privacy Policy link as an obstruction up to 80% of the viewport, while
  // retaining the stricter limit for generic host action bars.
  const privacyFooterHeight = measureCandidateHeight(privacyFooters, 0.8);

  return {
    bottomOffsetHeight: Math.max(actionBarHeight, privacyFooterHeight),
    privacyFooterHeight,
  };
}

export function measureHostBottomActionBarHeight(): number {
  return measureHostBottomObstructionHeights().bottomOffsetHeight;
}

export function useHostBottomObstructionHeights(enabled: boolean): HostBottomObstructionHeights {
  const [heights, setHeights] = useState(EMPTY_HOST_BOTTOM_OBSTRUCTION_HEIGHTS);

  useEffect(() => {
    if (!enabled) {
      setHeights(EMPTY_HOST_BOTTOM_OBSTRUCTION_HEIGHTS);
      return undefined;
    }

    let animationFrame = 0;
    const observedCandidates = new Set<HTMLElement>();
    const resizeObserver = new ResizeObserver(() => update());
    const syncObservedCandidates = () => {
      const nextCandidates = findHostBottomObstructionCandidates().candidates;
      for (const candidate of observedCandidates) {
        if (nextCandidates.has(candidate)) continue;
        resizeObserver.unobserve(candidate);
        observedCandidates.delete(candidate);
      }
      for (const candidate of nextCandidates) {
        if (observedCandidates.has(candidate)) continue;
        resizeObserver.observe(candidate);
        observedCandidates.add(candidate);
      }
    };
    const update = (event?: Event) => {
      const widgetRoot = document.getElementById("phoebe-chat-root");
      if (event?.type === "scroll" && event.target instanceof Node && widgetRoot?.contains(event.target)) return;
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        syncObservedCandidates();
        const nextHeights = measureHostBottomObstructionHeights();
        setHeights((currentHeights) =>
          currentHeights.bottomOffsetHeight === nextHeights.bottomOffsetHeight &&
          currentHeights.privacyFooterHeight === nextHeights.privacyFooterHeight
            ? currentHeights
            : nextHeights
        );
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const observer = new MutationObserver((mutations) => {
      const widgetRoot = document.getElementById("phoebe-chat-root");
      if (mutations.every((mutation) => widgetRoot?.contains(mutation.target))) return;
      update();
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
      childList: true,
      subtree: true,
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      resizeObserver.disconnect();
      observer.disconnect();
    };
  }, [enabled]);

  return heights;
}
